import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import clsx from "clsx";
import { useAuth } from "@flame/shared/auth";
import { lkSubs, lkWallet, lkAuth, ApiError } from "@flame/shared/api";
import { Button, Badge, Modal, toast, tariffBadgeVariant, statusBadgeVariant } from "@flame/shared/ui";
import { formatBytes, formatDate, daysUntil } from "@flame/shared/lib";
import { formatRuble } from "@flame/shared/utils";
import type { SubscriptionResponse, WalletTransaction } from "@flame/shared/types";

// ── Helpers ───────────────────────────────────────────────────────────────────

function TrafficBar({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const barColor =
    pct > 90 ? "bg-danger" : pct > 70 ? "bg-warn" : "bg-accent";
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
      <div
        className={clsx("h-full rounded-full transition-all duration-500", barColor)}
        style={{ width: `${pct}%` }}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
}

function txTypeLabel(type: string): string {
  switch (type) {
    case "topup_stars":    return "Пополнение Stars";
    case "monthly_debit":  return "Ежемесячное списание";
    case "admin_credit":   return "Зачисление администратором";
    case "admin_debit":    return "Списание администратором";
    case "refund":         return "Возврат";
    default:               return type;
  }
}

// ── Section skeleton ──────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return (
    <div className={clsx("animate-pulse rounded bg-border", className)} />
  );
}

// ── Balance card ──────────────────────────────────────────────────────────────

function BalanceCard({
  balance,
  onTopup,
}: {
  balance: number | undefined;
  onTopup: () => void;
}) {
  const hasBalance = balance !== undefined;

  return (
    <div className="flex items-center justify-between rounded-xl bg-surface p-5 shadow-card">
      <div>
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
          Баланс
        </p>
        {hasBalance ? (
          <>
            <p className="text-2xl font-semibold tabular-nums text-fg">
              {balance.toLocaleString("ru-RU")} YY
            </p>
            <p className="text-sm text-fg-muted">
              ≈ {formatRuble(balance)}
            </p>
          </>
        ) : (
          <>
            <Skeleton className="mb-1 h-7 w-28" />
            <Skeleton className="h-4 w-20" />
          </>
        )}
      </div>
      <Button variant="primary" size="md" onClick={onTopup}>
        Пополнить
      </Button>
    </div>
  );
}

// ── Topup info modal ──────────────────────────────────────────────────────────
// Online self-topup was retired 2026-06-17. Balance is credited manually by an
// administrator, so the button explains the flow instead of hitting a dead
// (410 Gone) endpoint.

function TopupInfoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Пополнение баланса"
      description="Баланс пополняет администратор."
    >
      <p className="text-sm text-fg-muted">
        Онлайн-оплата в FlameVPN отключена. Чтобы пополнить баланс, обратитесь
        к тому, кто выдал вам доступ к сервису — администратор начислит YY на ваш
        счёт вручную.
      </p>
      <div className="mt-5 flex justify-end">
        <Button variant="primary" size="md" onClick={onClose}>
          Понятно
        </Button>
      </div>
    </Modal>
  );
}

// ── Active subscription card ──────────────────────────────────────────────────

function ActiveSubCard({ sub }: { sub: SubscriptionResponse }) {
  const familyLabel = sub.tariff_family === "black" ? "Black" : "White";
  const tierLabel   = sub.tariff_tier   === "pro"   ? "Premium"   : "Basic";
  const days        = daysUntil(sub.expires_at);
  const subUrl      = sub.flame_sub_url ?? sub.sub_link ?? "";

  const handleCopy = async () => {
    if (!subUrl) return;
    try {
      await navigator.clipboard.writeText(subUrl);
      toast.success("Скопировано", "Ссылка на подписку скопирована");
    } catch {
      toast.error("Ошибка", "Не удалось скопировать ссылку");
    }
  };

  const openDeeplink = (app: "v2rayng" | "hiddify") => {
    if (!subUrl) return;
    const encoded = encodeURIComponent(subUrl);
    const link =
      app === "v2rayng"
        ? `v2rayng://install-config?url=${encoded}`
        : `hiddify://install-sub?url=${encoded}`;
    window.open(link, "_blank");
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-surface p-5 shadow-card">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Badge variant={tariffBadgeVariant(sub.tariff_family)}>
            {familyLabel}
          </Badge>
          <Badge variant={tariffBadgeVariant(sub.tariff_family, sub.tariff_tier)}>
            {tierLabel}
          </Badge>
        </div>
        <Badge variant={statusBadgeVariant(sub.status)}>
          {sub.status === "active" ? "Активна" : "Неактивна"}
        </Badge>
      </div>

      {/* Expiry */}
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-fg-muted">
            Действует до
          </p>
          <p className="text-sm font-medium text-fg">
            {formatDate(sub.expires_at)}
          </p>
        </div>
        <span
          className={clsx(
            "text-xs tabular-nums",
            days < 7 ? "text-danger" : days < 30 ? "text-warn" : "text-fg-muted",
          )}
        >
          {days < 0 ? "истёк" : `${days} дн.`}
        </span>
      </div>

      {/* Traffic */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <p className="text-xs uppercase tracking-wide text-fg-muted">Трафик</p>
          <span className="text-xs tabular-nums text-fg">
            {formatBytes(sub.traffic_used_bytes)}
            <span className="text-fg-muted">
              {" / "}
              {formatBytes(sub.traffic_limit_bytes)}
            </span>
          </span>
        </div>
        <TrafficBar
          used={sub.traffic_used_bytes}
          limit={sub.traffic_limit_bytes}
        />
        <p className="text-xs text-fg-subtle">
          Сброс: {formatDate(sub.traffic_resets_at)}
        </p>
      </div>

      {/* Actions */}
      {subUrl && (
        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <Button
            variant="secondary"
            size="md"
            className="w-full"
            onClick={() => void handleCopy()}
          >
            <CopyIcon />
            Скопировать подписку
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeeplink("v2rayng")}
            >
              v2rayNG
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeeplink("hiddify")}
            >
              Hiddify
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline icon ───────────────────────────────────────────────────────────────

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="4" y="4" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M2 10V3a1 1 0 0 1 1-1h7"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Recent transactions row ───────────────────────────────────────────────────

function TxRow({ tx }: { tx: WalletTransaction }) {
  const isCredit = tx.amount >= 0;
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm text-fg">{txTypeLabel(tx.type)}</p>
        <p className="text-xs text-fg-subtle">
          {formatDate(tx.created_at)}
        </p>
      </div>
      <span
        className={clsx(
          "ml-4 shrink-0 text-sm font-medium tabular-nums",
          isCredit ? "text-success" : "text-fg-muted",
        )}
      >
        {isCredit ? "+" : ""}
        {tx.amount.toLocaleString("ru-RU")} YY
      </span>
    </div>
  );
}

// ── Quick link row ────────────────────────────────────────────────────────────

function QuickLink({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg bg-surface px-4 py-3 shadow-card transition-colors hover:bg-surface-elevated"
    >
      <span className="flex items-center gap-3 text-sm text-fg">
        <span className="text-fg-muted">{icon}</span>
        {label}
      </span>
      <ChevronRight />
    </Link>
  );
}

function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M5 2l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Security (2FA) card ───────────────────────────────────────────────────────

function SecurityCard({ totpEnabled }: { totpEnabled: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (confirmOpen) {
      // Focus on next tick so the modal mount has settled.
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setCode("");
      setFieldError(null);
    }
  }, [confirmOpen]);

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setFieldError("Введите ровно 6 цифр");
      inputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setFieldError(null);
    try {
      // Re-verify session to pick up totp_verified=true (no-op if already verified).
      await lkAuth.totpVerify(code);
      await lkAuth.totpDisable();
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("2FA отключена", "Вход теперь без второго фактора");
      setConfirmOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError("Неверный код");
      } else {
        setFieldError("Ошибка отключения 2FA");
      }
      setCode("");
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-label="Безопасность">
      <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
        Безопасность
      </p>
      <div className="flex items-center justify-between gap-3 rounded-xl bg-surface p-5 shadow-card">
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg">Двухфакторная аутентификация</p>
          {totpEnabled ? (
            <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-success">
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
              Включена
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-fg-subtle">Не включена</p>
          )}
        </div>
        {totpEnabled ? (
          <Button
            variant="secondary"
            size="md"
            onClick={() => setConfirmOpen(true)}
          >
            Отключить
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={() => void navigate("/totp/setup")}
          >
            Включить 2FA
          </Button>
        )}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Отключить 2FA?"
        description="Отключение двухфакторной аутентификации снизит защиту аккаунта. Введите текущий 6-значный код из приложения, чтобы подтвердить."
      >
        <form onSubmit={(e) => void handleDisable(e)} noValidate>
          <label className="sr-only" htmlFor="disable-totp-code">Код TOTP</label>
          <input
            ref={inputRef}
            id="disable-totp-code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              setFieldError(null);
            }}
            autoComplete="one-time-code"
            placeholder="000000"
            className={clsx(
              "mono w-full rounded-md border bg-surface-elevated px-3 py-2.5 text-center text-xl tracking-[0.4em]",
              "text-fg caret-accent placeholder:text-fg-subtle",
              "outline-none transition-colors",
              fieldError ? "border-danger" : "border-border focus:border-accent",
            )}
          />
          {fieldError && (
            <p role="alert" className="mt-1.5 text-xs text-danger">{fieldError}</p>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="md"
              loading={submitting}
              disabled={!/^\d{6}$/.test(code) || submitting}
            >
              Отключить 2FA
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AccountPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  const {
    data: subsData,
    isLoading: subsLoading,
    isError: subsError,
  } = useQuery({
    queryKey: ["subs"],
    queryFn: lkSubs.listSubscriptions,
  });

  const {
    data: txData,
    isLoading: txLoading,
  } = useQuery({
    queryKey: ["wallet-transactions", 5],
    queryFn: () => lkWallet.listTransactions(5),
    // Silently no-op if endpoint not yet implemented
    retry: false,
  });

  const [topupOpen, setTopupOpen] = useState(false);

  // Redirect to onboarding if no subscriptions
  useEffect(() => {
    if (
      !subsLoading &&
      !subsError &&
      subsData &&
      subsData.items.length === 0
    ) {
      void navigate("/onboarding", { replace: true });
    }
  }, [subsLoading, subsError, subsData, navigate]);

  const user = me?.user;
  const displayName =
    user?.first_name ??
    (user?.username ? `@${user.username}` : null) ??
    (user?.tg_id ? `tg:${user.tg_id}` : "Пользователь");

  // Render every active subscription (a user may hold both WHITE and BLACK).
  // Fall back to a single inactive/suspended row when there is no active one,
  // so the UI still has something to show before the onboarding redirect.
  const activeSubs = subsData?.items.filter((s) => s.status === "active") ?? [];
  const subsToShow =
    activeSubs.length > 0
      ? activeSubs
      : subsData?.items[0]
        ? [subsData.items[0]]
        : [];
  const balance = me?.balance_yy;

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-5">
      {/* ── Hero greeting ── */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="truncate text-lg font-semibold text-fg">
            Привет, {displayName}
          </h2>
          {user?.tg_id && (
            <p className="mt-0.5 text-xs text-fg-subtle">
              tg_id: <span className="font-mono">{user.tg_id}</span>
            </p>
          )}
        </div>
        {user?.created_at && (
          <Badge variant="neutral">
            с {formatDate(user.created_at)}
          </Badge>
        )}
      </div>

      {/* ── Balance card ── */}
      <BalanceCard balance={balance} onTopup={() => setTopupOpen(true)} />

      <TopupInfoModal open={topupOpen} onClose={() => setTopupOpen(false)} />

      {/* ── Active subscriptions (WHITE + BLACK) ── */}
      <section aria-label="Подписки">
        <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
          {subsToShow.length > 1 ? "Активные подписки" : "Активная подписка"}
        </p>
        {subsLoading && (
          <div className="flex flex-col gap-3 rounded-xl bg-surface p-5 shadow-card">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-1.5 w-full" />
          </div>
        )}
        {subsError && !subsLoading && (
          <p className="text-sm text-danger">
            Не удалось загрузить подписки
          </p>
        )}
        <div className="flex flex-col gap-3">
          {subsToShow.map((sub) => (
            <ActiveSubCard key={sub.id} sub={sub} />
          ))}
        </div>
      </section>

      {/* ── Recent transactions ── */}
      <section aria-label="Последние операции">
        <div className="mb-2.5 flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
            Последние операции
          </p>
          <Link
            to="/wallet"
            className="text-xs text-accent transition-colors hover:text-accent-hover"
          >
            Все операции
          </Link>
        </div>
        <div className="rounded-xl bg-surface shadow-card divide-y divide-border overflow-hidden">
          {txLoading && (
            <div className="flex flex-col divide-y divide-border">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          )}
          {!txLoading && (!txData || txData.items.length === 0) && (
            <p className="px-4 py-6 text-center text-sm text-fg-subtle">
              Операций пока нет
            </p>
          )}
          {!txLoading && txData && txData.items.length > 0 && (
            <div className="divide-y divide-border">
              {txData.items.map((tx) => (
                <div key={tx.id} className="px-4">
                  <TxRow tx={tx} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Security (2FA) ── */}
      <SecurityCard totpEnabled={me?.totp_enabled ?? false} />

      {/* ── Quick links ── */}
      <section aria-label="Быстрые ссылки">
        <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
          Действия
        </p>
        <div className="flex flex-col gap-2">
          <QuickLink
            to="/wallet"
            label="Полная история операций"
            icon={<HistoryIcon />}
          />
          <QuickLink
            to="/onboarding"
            label="Активировать инвайт"
            icon={<KeyIcon />}
          />
          <a
            href="https://t.me/AlertVPSV_Bot"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-lg bg-surface px-4 py-3 shadow-card transition-colors hover:bg-surface-elevated"
          >
            <span className="flex items-center gap-3 text-sm text-fg">
              <span className="text-fg-muted">
                <SupportIcon />
              </span>
              Поддержка
            </span>
            <ChevronRight />
          </a>
        </div>
      </section>
    </div>
  );
}

// ── Inline icons ──────────────────────────────────────────────────────────────

function HistoryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.25" />
      <path d="M7 4.5V7l2 1.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="5" cy="7" r="3" stroke="currentColor" strokeWidth="1.25" />
      <path d="M8 7h4M10 5.5V7" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M7 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM7 9.5V7M7 5.5h.01"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}
